import * as React from 'react';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import { SourceCitation, isInternalHref } from './sourceCitations';

type Props = {
  sources: SourceCitation[];
  onNavigate?: (route: string) => void;
};

// The citations the assistant used, as chips rather than hyperlinks buried in
// the prose. Built on `.ai-chat-chip` — a source is a "go there" affordance,
// exactly like a goto quick action.
//
// The two kinds of source do different things when clicked, and used to look
// identical while doing them (F13). Your company's own documents open inside
// the Hub; Flair help articles open a new tab. So they now differ on three
// axes at once, because colour alone can't carry it:
//
//   your company  →  tinted fill, page icon,      → chevron  (stays here)
//   Flair help    →  plain outline, book icon,    ↗ arrow    (new tab)
const AiChatSourceLinks: React.FC<React.PropsWithChildren<Props>> = ({
  sources,
  onNavigate,
}) => {
  const { t } = useTranslation();
  if (sources.length === 0) return null;

  // Your own documents first: they are the authority, the help article explains
  // the mechanics. Stable sort, so citation order survives within each group.
  const ordered = [...sources].sort(
    (a, b) => Number(isInternalHref(b.href)) - Number(isInternalHref(a.href)),
  );

  return (
    <div className="ai-chat-source-links">
      {ordered.map((source) => {
        const internal = isInternalHref(source.href);
        const chipClass = `ai-chat-chip ai-chat-chip--source ai-chat-chip--source-${
          internal ? 'internal' : 'external'
        }`;
        const content = (
          <>
            <FlairIcon
              icon={internal ? 'phosphor-file-text' : 'phosphor-book-open'}
              size="xs"
            />
            <span className="ai-chat-chip__label">{source.label}</span>
            <span aria-hidden="true" className="ai-chat-chip__arrow">
              {internal ? '→' : '↗'}
            </span>
          </>
        );

        // Hub documents navigate in-app; a full page load would close the chat.
        if (internal && onNavigate) {
          return (
            <button
              key={source.href}
              type="button"
              className={chipClass}
              onClick={() => onNavigate(source.href)}>
              {content}
            </button>
          );
        }

        return (
          <a
            key={source.href}
            href={source.href}
            target="_blank"
            rel="noopener noreferrer"
            className={chipClass}
            aria-label={`${source.label} ${t('aiChat.sources.opensInNewTab')}`}>
            {content}
          </a>
        );
      })}
    </div>
  );
};

export default AiChatSourceLinks;
